import { type CyberAssessmentSelectDto } from '@@types/api.types';
import { AssessmentLayout } from '@components/layouts/assessment.layout';
import { AssessmentSidebar } from '@components/pages/assessment-shell/components/assessment-sidebar.component';
import { useIsMobile } from '@hooks/mobile.hook';
import { useSafeReturnTo } from '@hooks/return-to.hook';
import { setReturnToSearchParam } from '@hooks/return-to.hook';
import { useIsTablet } from '@hooks/tablet.hook';
import { errorToast } from '@hooks/toast.hook';
import { useCompleteCyberAssessment } from '@queries/cyber-assessment.query';
import { useGetTechnology } from '@queries/technology.query';
import { useSurveySidebarStore } from '@stores/survey-sidebar.store';
import {
  resolveSurveySidebarBaseStateForSet,
  resolveSurveySidebarBaseStateForToggle,
  resolveSurveySidebarState,
  resolveSurveySidebarViewportMode,
} from '@stores/survey-sidebar.util';
import {
  buildCyberAssessmentShellModel,
  getCyberAssessmentChapterProgress,
  getCyberAssessmentShellProgress,
} from '@utils/cyber-assessment/model.util';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type CyberAssessmentLayoutProps = {
  children: React.ReactNode;
  cyberAssessment: CyberAssessmentSelectDto;
  currentChapterId?: string;
  currentSectionId?: string;
};

export const CyberAssessmentLayout: React.FC<CyberAssessmentLayoutProps> = (props) => {
  const { children, currentChapterId, currentSectionId, cyberAssessment } = props;
  const { cyberAssessmentId, technologyId } = useParams();
  const navigate = useNavigate();
  const { returnTo } = useSafeReturnTo();
  const { data: technology } = useGetTechnology(technologyId);
  const { mutateAsync: completeCyberAssessment, isPending: isCompletingCyberAssessment } =
    useCompleteCyberAssessment(cyberAssessmentId);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const viewportMode = resolveSurveySidebarViewportMode({ isMobile, isTablet });
  const { baseIsCollapsed, baseIsContextCollapsed, reset, setBaseState } = useSurveySidebarStore();
  const { isCollapsed } = resolveSurveySidebarState({
    baseIsCollapsed,
    baseIsContextCollapsed,
    viewportMode,
  });
  const shellModel = useMemo(() => buildCyberAssessmentShellModel(cyberAssessment), [cyberAssessment]);

  const currentChapter =
    shellModel.chapters.find((chapter) => chapter.id === currentChapterId) ?? shellModel.chapters[0];
  const sidebarChapters = shellModel.chapters.map((chapter) => {
    const chapterSearchParams = new URLSearchParams();

    chapterSearchParams.set('chapterId', chapter.id);
    setReturnToSearchParam({ returnTo, searchParams: chapterSearchParams });

    return {
      href: `/technology/${technologyId}/cyber-assessment/${cyberAssessment.id}?${chapterSearchParams.toString()}`,
      id: chapter.id,
      progress: getCyberAssessmentChapterProgress(chapter),
      sections: chapter.sections.map((section) => ({
        answered: section.answered,
        href: `/technology/${technologyId}/cyber-assessment/${cyberAssessment.id}/section/${section.id}${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`,
        id: section.id,
        title: section.title,
        total: section.total,
      })),
      title: chapter.title,
    };
  });
  const canCompleteAssessment =
    shellModel.totalQuestions > 0 && shellModel.answeredQuestions === shellModel.totalQuestions;

  const handleToggleCollapsed = (): void => {
    setBaseState(
      resolveSurveySidebarBaseStateForToggle({
        baseIsCollapsed,
        baseIsContextCollapsed,
        target: 'left',
        viewportMode,
      }),
    );
  };

  const handleSetIsCollapsed = (collapsed: boolean): void => {
    setBaseState(
      resolveSurveySidebarBaseStateForSet({
        baseIsCollapsed,
        baseIsContextCollapsed,
        collapsed,
        target: 'left',
        viewportMode,
      }),
    );
  };

  const handleExit = (): void => {
    reset();

    if (returnTo) {
      navigate(returnTo);
      return;
    }

    navigate(`/technology/${technologyId}?tab=cyber&cyberId=${cyberAssessment.id}`);
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      await completeCyberAssessment();
      reset();
      navigate(`/technology/${technologyId}?tab=cyber&cyberId=${cyberAssessment.id}`);
    } catch {
      errorToast({ title: 'Failed to complete cyber assessment. Please try again.' });
    }
  };

  return (
    <AssessmentLayout
      assessmentLabel="Cyber Assessment"
      leftSidebar={
        <AssessmentSidebar
          chapters={sidebarChapters}
          currentChapterId={currentChapter?.id}
          currentSectionId={currentSectionId}
          isCollapsed={isCollapsed}
          isSubmitting={isCompletingCyberAssessment}
          onNavigate={navigate}
          onSubmit={cyberAssessment.status === 'DRAFT' ? () => void handleSubmit() : undefined}
          setIsCollapsed={handleSetIsCollapsed}
          submitDisabled={!canCompleteAssessment}
          submitLabel="Complete"
          submitTooltip={canCompleteAssessment ? undefined : 'Every question must be answered before completing'}
          totalProgress={getCyberAssessmentShellProgress(shellModel)}
        />
      }
      onExit={handleExit}
      onToggleLeft={handleToggleCollapsed}
      statusLabel={cyberAssessment.status === 'DRAFT' ? 'Draft' : 'Completed'}
      technologyName={technology?.name}
    >
      {children}
    </AssessmentLayout>
  );
};
